import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import * as loginService from "../../../Services/loginService";
import * as Utils from "../../../Utils";
import * as Context from "../../../Context";
import * as routes from "../../../routes";
import { MESSAGES } from "../constants";
import { PinInput } from "../../../Components/Common";
import { default as solarParkImg2 } from "../../../Assets/Solar_Park2.jpg";
import { default as GreenEn_Logo } from "../../../Assets/GreenEn_Logo.png"
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

const LoginForm = () => {
  const [error, setError] = useState("");
  const { setUser } = useContext(Context.UserContext);
  const navigate = useNavigate();

  const userSchema = Yup.object({
    mobile: Yup.string()
      .matches(/^\d{10}$/, MESSAGES.MOBILE_VALIDATION)
      .required(MESSAGES.REQUIRED),
    password: Yup.string()
      .matches(/^\d{4}$/, MESSAGES.PIN_VALIDATION)
      .required(MESSAGES.REQUIRED),
  });

  const clearForm = (values) => {
    values.mobile = "";
    values.password = "";
  };

  const onSubmit = (values, { setSubmitting }) => {
    loginService
      .login(values)
      .then((response) => {
        if (response.status === 200 && response.data) {
          Utils.setUser(response.data);
          setUser(response.data);
          navigate(routes.BASE, { replace: true });
          toast.success(MESSAGES.LOGIN_SUCCESS);
        } else if (response.error) {
          const msg = extractErrorMessage(response) || MESSAGES.LOGIN_FAILURE;
          setError(msg);
          clearForm(values);
          toast.error(msg);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <div className="relative min-h-[100dvh] min-h-[calc(100dvh-constant(safe-area-inset-bottom))] min-h-[calc(100dvh-env(safe-area-inset-bottom))] w-full flex justify-center items-center lg:bg-slate-100">
        {/* Mobile Background Image */}
        <div
          className="lg:hidden absolute inset-0 bg-cover bg-center opacity-80 z-0"
          style={{ backgroundImage: `url(${solarParkImg2})` }}
        ></div>

        <div className="relative z-10 flex gap-2 flex-col md:w-4/5 m-4 p-5 bg-white/80 md:bg-white shadow-md items-center justify-center border rounded-lg border-gray">
          <div>
            <div className="flex items-center gap-4">
              <img
                src={GreenEn_Logo}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
              <p className="text-4xl text-primary font-semibold">{process.env.REACT_APP_TITLE}</p>
            </div>
            <p className="text-center text-3xl text-secondary mt-6 font-semibold">
              LOGIN
            </p>
          </div>

          <div className="w-full">
            <Formik
              initialValues={{ mobile: "", password: "" }}
              validationSchema={userSchema}
              onSubmit={onSubmit}
            >
              {({ isSubmitting }) => (
                <Form
                  className="flex flex-col gap-4 w-full text-secondary"
                  autoComplete="off"
                >
                  <div className="w-full font-semibold">
                    <label>Mobile</label>
                    <div className="w-full">
                      <Field name="mobile">
                        {({ field }) => (
                          <input
                            {...field}
                            type="number"
                            placeholder="Enter your mobile number"
                            onChange={(e) => {
                              field.onChange(e);
                              setError("");
                            }}
                            className="w-full py-4 lg:py-2 px-3 text-gray-700 focus:shadow-outline bg-white border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-secondary peer"
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="mobile"
                        component="p"
                        className="text-red-500 text-xs italic"
                      />
                    </div>
                  </div>

                  {/* PIN Input Field */}
                  <PinInput name="password" label="PIN" />

                  <div className="flex flex-col gap-2 items-center justify-center">
                    {error && (
                      <p className="text-red-500 italic text-center text-sm">
                        {error}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="cursor-pointer w-full bg-primary hover:bg-secondary text-white p-2 rounded focus:outline-none focus:shadow-outline"
                    >
                      Log In
                    </button>
                    <div className="flex items-center gap-2 justify-center text-center mt-2 text-sm text-secondary">
                      <div>Don't have an account?</div>
                      <Link
                        to={routes.SIGNUP}
                        className="font-semibold leading-6 text-secondary hover:text-primary md:text-primary md:hover:text-secondary"
                      >
                        Create an Account!
                      </Link>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
